// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { IState } from './index';
import { getCountriesAsync, getLinkGroupsAsync, getDefaultCountryAsync } from '@/api/geolocationApi';
import { isError } from '@/utils/result';

const namespaced = (val: string) => `geolocation/${val}`;

export const getterTypes = Object.freeze({
    COUNTRY_BY_CODE: namespaced('countryByCode'),
    CONDITIONS_OF_USE_LINK: namespaced('conditionsOfUseLink'),
    PRIVACY_LINK: namespaced('privacyLink'),
    SALE_TERMS_LINK: namespaced('saleTermsLink'),
});

export const actionTypes = Object.freeze({
    LOAD_COUNTRIES: namespaced('loadCountries'),
    LOAD_LINKED_GROUPS: namespaced('loadLinkGroups'),
});

export const mutationTypes = Object.freeze({
    SET_COUNTRIES: namespaced('setCountries'),
    SET_LINKED_GROUPS: namespaced('setLinkGroups'),
    SET_DEFAULT_COUNTRY_CODE: namespaced('setDefaultCountryCode'),
});

export class State {
    countries: Array<{ code: string, name: string, region: string, linkGroup: string }>;
    linkGroups: Array<{ linkGroup: string, links: Array<{ code: string, address: any }> }>;
    defaultCountryCode: string;
    defaultRegionCode: string;
    loadingCountries: boolean;
    loadingLinkGroups: boolean;

    constructor() {
        this.countries = [];
        this.linkGroups = [];
        this.defaultCountryCode = 'US';
        this.defaultRegionCode = 'AMER';
        this.loadingCountries = false;
        this.loadingLinkGroups = false;
    }
}

export const LinkCodes = Object.freeze({
    Terms: 'terms-and-conditions',
    Privacy: 'privacy-policy',
    ConditionsOfUse: 'conditions-of-use',
    RequestMaterials: 'request-csmi',
});

const getLinkGroup = (state: State, countryCode: string) => {
    if (state.countries.length === 0 || state.linkGroups.length === 0) {
        return null;
    }
    const country = state.countries.find((x) => x.code === countryCode.toUpperCase());
    if (!country) {
        return null;
    }
    const group = state.linkGroups.find((x) => x.linkGroup === country.linkGroup);

    return group;
};

export const actions: ActionTree<State, IState> = {
    async loadCountries({ commit, state }) {
        if (state.loadingCountries || state.countries.length > 0) {
            return;
        }

        commit('setLoadingCountries', true);

        const getCountriesResult = await getCountriesAsync();
        if (isError(getCountriesResult)) {
            commit('setLoadingCountries', false);
            throw new Error('failed to get countries');
        }

        const getDefaultCountryResult = await getDefaultCountryAsync();
        if (isError(getDefaultCountryResult)) {
            commit('setLoadingCountries', false);
            throw new Error('failed to get default country');
        }

        commit('setCountries', getCountriesResult);
        commit('setDefaultCountryCode', getDefaultCountryResult);

        const country = getCountriesResult.find(
            (c) => c.code === getDefaultCountryResult
        );
        if (country) {
            commit('setDefaultRegionCode', country.region);
        }

        commit('setLoadingCountries', false);
    },
    async loadLinkGroups({ commit, state }) {
        if (state.loadingLinkGroups || state.linkGroups.length > 0) {
            return;
        }
        commit('setLoadingLinkGroups', true);
        const response = await getLinkGroupsAsync();

        if (isError(response)) {
            commit('setLoadingLinkGroups', false);
            throw new Error('failed to get link groups');
        }

        commit('setLinkGroups', response);
        commit('setLoadingLinkGroups', false);
    },
};

export const mutations: MutationTree<State> = {
    setLoadingCountries(state, value) {
        state.loadingCountries = value;
    },
    setCountries(state, countries) {
        state.countries = countries;
    },
    setLoadingLinkGroups(state, value) {
        state.loadingLinkGroups = value;
    },
    setLinkGroups(state, linkGroups) {
        state.linkGroups = linkGroups;
    },
    setDefaultCountryCode(state, newCountryCode) {
        state.defaultCountryCode = newCountryCode;
    },
    setDefaultRegionCode(state, newRegionCode) {
        state.defaultRegionCode = newRegionCode;
    },
};

export const getters: GetterTree<State, IState> = {
    countryByCode: (state) => (countryIsoCode: string) => state.countries.find((c) => countryIsoCode === c.code),
    privacyLink: (state) => (countryCode: string) => {
        const linkGroup = getLinkGroup(state, countryCode);
        return linkGroup
            ? linkGroup.links.find((x) => x.code === LinkCodes.Privacy)?.address
            : null;
    },
    saleTermsLink: (state) => (countryCode: string) => {
        const linkGroup = getLinkGroup(state, countryCode);
        return linkGroup
            ? linkGroup.links.find((x) => x.code === LinkCodes.Terms)?.address
            : null;
    },
    conditionsOfUseLink: (state) => (countryCode: string) => {
        const linkGroup = getLinkGroup(state, countryCode);
        return linkGroup
            ? linkGroup.links.find((x) => x.code === LinkCodes.ConditionsOfUse)?.address
            : null;
    },
};

export default {
    namespaced: true,
    state: new State(),
    actions,
    mutations,
    getters,
};
